import { jwtDecode } from 'jwt-decode';
import DuLogger from './DuLogger';

/**
 * @description Function to get token from Authorization header
 * @param {string} authorizationHeader Authorization Header
 * @return {string}
 */
const GetTokenFromAuth = (authorizationHeader) => {
    if (typeof authorizationHeader !== 'string') {
        return null;
    }

    return authorizationHeader.includes('Bearer ') || authorizationHeader.includes('Basic ') ? authorizationHeader.split(' ')[1] : authorizationHeader;
};

/**
 * @description Function to get groups that are assigned to token
 * @param {*} token Token
 * @return {array} Array of strings
 */
var GetGroups = function (token) {
    var decodedToken = DecodeToken(token);

    if (window && window.newrelic) {
        window.newrelic.setCustomAttribute('OktaUserId', decodedToken.uid || '');
    }

    return decodedToken.groups || [];
};

/**
 * @description Function to find if token is part of provided list of groups
 * @param {*} token Token
 * @param {array} allowedGroups List of allowed groups
 * @return {boolean}
 */
var IsInGroup = function (token, allowedGroups) {
    var groups = GetGroups(token);
    return groups.some((group) => allowedGroups.indexOf(group) >= 0);
};

/**
 * @description Function to get email address from token
 * @param {*} token Token
 * @return {string}
 */
var GetEmail = function (token) {
    var decodedToken = DecodeToken(token);
    return decodedToken.sub || decodedToken.email || '';
};

/**
 * @description Function to get OktaId from token
 * @param {*} token Token
 * @return {string}
 */
var GetOktaId = function (token) {
    var decodedToken = DecodeToken(token);
    return decodedToken.uid || '';
};

/**
 * @description Function to get UserId from token
 * @param {*} token Token
 * @return {string}
 */
var GetUserId = function (token) {
    var decodedToken = DecodeToken(token);
    var email = decodedToken.sub || decodedToken.email || '';

    if (email.length > 0) {
        return email.substr(0, email.indexOf('@'));
    }

    return null;
};

/**
 * @description Function to get expiry date of token
 * @param {*} token Token
 * @return {date}
 */
var GetExpireDate = function (token) {
    var decodedToken = DecodeToken(token);
    var exp = decodedToken.exp || 0;

    if (exp > 0) {
        return new Date(exp * 1000);
    }

    return null;
};

/**
 * @description Function to check if token is valid
 * @param {*} token Token
 * @param {number} gracePeriodInMinutes
 * @return {boolean}
 */
var IsTokenValid = function (token, gracePeriodInMinutes) {
    var expDate = GetExpireDate(token);

    if (expDate === null) {
        return false;
    }

    const difference = expDate - new Date();

    if (difference < gracePeriodInMinutes * 60000) {
        return false;
    }

    return true;
};

/**
 * @description Function to get value of token
 * @param {*} token Token object (or) Token value
 * @return {string}
 */
const GetTokenValue = (token) => {
    if (typeof token === 'object') {
        return token.value || token.accessToken || '';
    }
    return token || '';
};

/**
 * @description Function to validate a token by decoding it
 * @param {*} token Token object (or) Token value
 * @return {string}
 */
var DecodeToken = function (token) {
    var decodedToken = {};
    const tokenValue = GetTokenValue(token);

    try {
        decodedToken = jwtDecode(tokenValue);
    } catch (error) {
        DuLogger.warn('Found an invalid token, this is only okay if we are logging a user out or give them a not authorized page.', token);
    }

    return decodedToken;
};

/**
 * @description Function to get group scope based on environment
 * @param {string} location
 * @return {string}
 */
var GetGroupScope = function (location) {
    var environment = '';
    var hostname = location ? location : window ? window.location.hostname.toLowerCase() : '';

    if (hostname.includes('dev') || hostname.includes('localhost')) {
        environment = 'dev';
    } else if (hostname.includes('stage')) {
        environment = 'stage';
    } else if (hostname.includes('uat')) {
        environment = 'uat';
    } else if (hostname.includes('qa')) {
        environment = 'qa';
    } else if (hostname.includes('tst') || hostname.includes('test')) {
        environment = 'test';
    } else {
        environment = 'prod';
    }

    return environment;
};

/**
 * @description Function to check if token has write access
 * @param {object} context
 * @param {*} token
 * @param {array} adminGroups
 * @param {array} regularGroups
 * @return {boolean}
 */
var ValidateTokenWithWriteScope = function (context, token, adminGroups, regularGroups) {
    const tokenValue = GetTokenValue(token);

    if (!tokenValue || tokenValue.length <= 0) {
        if (context.state.canWrite === null || context.state.canWrite === true) {
            context.setState({ canWrite: false });
        }

        return false;
    }

    if (window && window.newrelic) {
        window.newrelic.setCustomAttribute('OktaUserId', GetOktaId(token));
    }

    adminGroups = adminGroups ? adminGroups : window && window.config ? window.config.OKTA_ADMIN_GROUPS || [] : [];
    regularGroups = regularGroups ? regularGroups : window && window.config ? window.config.OKTA_REGULAR_GROUPS || [] : [];

    if (IsInGroup(token, adminGroups) || IsInGroup(token, regularGroups)) {
        if (context.state.canWrite === null || context.state.canWrite === false) {
            context.setState({ canWrite: true });
        }
    } else {
        if (context.state.canWrite === null || context.state.canWrite === true) {
            context.setState({ canWrite: false });
        }
    }

    return true; //Return true because token is valid, don't care about canWrite for the return
};

/** @type {*} */
const DuAuthenticationUtilities = {
    OnAuthRequired: ({ history }) => history.push('/'),
    GetTokenFromAuth,
    GetTokenValue,
    GetGroups,
    IsInGroup,
    GetEmail,
    GetOktaId,
    GetUserId,
    GetGroupScope,
    GetExpireDate,
    IsTokenValid,
    DecodeToken,
    ValidateTokenWithWriteScope
};

export default DuAuthenticationUtilities;
